import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useFavorites } from '../context/FavoritesContext';
import { Heart, MapPin, Trash2, ExternalLink } from 'lucide-react';

const FavoritesPage = () => {
  const { favorites, getFavoriteOffers, clearFavorites, toggleFavorite, favoritesCount } = useFavorites();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavoriteOffers();
  }, [favorites]);

  const loadFavoriteOffers = async () => {
    setLoading(true);
    const favoriteOffers = await getFavoriteOffers();
    setOffers(favoriteOffers);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-blue-200">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-blue-200">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-red-500 via-purple-600 to-pink-500 bg-clip-text text-transparent mb-3">
                  Mes Favoris ⭐
                </h1>
                <p className="text-gray-600">
                  {favoritesCount} offre{favoritesCount > 1 ? 's' : ''} sauvegardée{favoritesCount > 1 ? 's' : ''}
                </p>
              </div>
              {favoritesCount > 0 && (
                <Button
                  variant="destructive"
                  onClick={clearFavorites}
                  className="gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Tout effacer
                </Button>
              )}
            </div>
          </div>

          {/* Empty State */}
          {favoritesCount === 0 ? (
            <Card className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                  <Heart className="w-12 h-12 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Aucun favori</h3>
                  <p className="text-gray-600 mb-4">
                    Commencez à ajouter des offres à vos favoris !
                  </p>
                  <Link to="/carte">
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-500">
                      <MapPin className="w-4 h-4 mr-2" />
                      Explorer la carte
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.map((offer) => (
                <Card key={offer.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  {offer.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={offer.image}
                        alt={offer.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg line-clamp-2">{offer.title}</CardTitle>
                      <button
                        onClick={() => toggleFavorite(offer.id)}
                        className="flex-shrink-0"
                      >
                        <Heart className="w-6 h-6 fill-red-500 text-red-500 hover:scale-110 transition-transform" />
                      </button>
                    </div>
                    <Badge className="w-fit mt-2">{offer.subcategory}</Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {offer.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{offer.address}</span>
                    </div>
                    <Link to={`/offre/${offer.id}`}>
                      <Button size="sm" className="w-full gap-2 bg-gradient-to-r from-purple-600 to-pink-500">
                        <ExternalLink className="w-4 h-4" />
                        Voir Détails
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FavoritesPage;
